/* eslint-disable no-console */
/* eslint no-underscore-dangle: ["error", { "allow": ["__typename"] }] */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
// graphQL
import gql from 'graphql-tag';
// adopt
import { adopt } from 'react-adopt';
// global variables
import { defaultNumberOfRows as DEFAULT_NUMBER_OF_ROWS } from 'config';
// our components
import RouterContext from 'helpers/routerContext';
import qs, { getQueryString } from 'helpers/queryStrings';
import { PageInfo } from 'components/common/Paginator';
import { Query, Mutation } from 'components/common/Query';
import UserList from './List';

/**
 * Query to fetch users from api
 */
export const ADMIN_USERS_QUERY = gql`
  query UserList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $rank: [FilterRank]!
    $isActive: Boolean
    $permissionGroup: ID
    $contactInfo: String
    $userType: [FilterUserType]
  ) {
    adminUsers(
      first: $first
      after: $after
      last: $last
      before: $before
      rank: $rank
      isActive: $isActive
      permissionGroup: $permissionGroup
      contactInfo: $contactInfo
      userType: $userType
    ) @connection(key: "users") {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          fullname
          email
          phone
          rank
          userType
          photo {
            gcsKey
            url
          }
          isActive
        }
      }
    }
  }
  ${PageInfo}
`;

/**
 * Mutation to delete users
 */
export const DELETE_USERS_MUTATION = gql`
  mutation($ids: [ID]!) {
    deleteAdminUser(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to block users
 */
export const BLOCK_USERS_MUTATION = gql`
  mutation($ids: [ID]!) {
    deactivateUsers(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * Mutation to activate users
 */
export const ACTIVE_USERS_MUTATION = gql`
  mutation($ids: [ID]!) {
    activateUsers(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

/**
 * @class
 * @extends Component
 */
class UserListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { initialFilters } = props;
    const {
      numberOfRowsSelected,
      direction,
      cursor,
      ...initFilters
    } = initialFilters;

    const defaultFilters = {
      rank: [],
      userType: [],
      isActive: undefined,
      permissionGroup: undefined,
    };

    this.state = {
      textToSearch: initialFilters.contactInfo || '',
      filters: { ...defaultFilters, ...initFilters },
      numberOfRowsSelected:
        parseInt(numberOfRowsSelected, 0) || DEFAULT_NUMBER_OF_ROWS,
      itemProcessing: [],
      failedIds: [],
      showNotification: false,
      pagination: { direction: direction || null, cursor: cursor || undefined },
    };

    this.Compose = adopt({
      mutateUserDelete: this.userDelete,
      mutateUserBlock: this.userBlock,
      mutateUserUnBlock: this.userUnBlock,
      query: this.listUsers,
    });
    this.fetchMore = () => {};
  }

  /**
   * @description
   * Query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  listUsers = ({ render }) => {
    const { numberOfRowsSelected, filters, pagination } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };
    return (
      <Query
        query={ADMIN_USERS_QUERY}
        variables={{ ...paginationVariables, ...filters }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, fetchMore }) => {
          this.fetchMore = fetchMore;
          return render({ data, loading, fetchMore });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Mutation delete callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  userDelete = ({ render }) => {
    const { itemProcessing } = this.state;
    return (
      <Mutation
        mutation={DELETE_USERS_MUTATION}
        onCompleted={this.mutationCompleted('deleteAdminUser', itemProcessing)}
      >
        {handleUserDelete => render({ handleUserDelete })}
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation block callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  userBlock = ({ render }) => {
    const { itemProcessing, lastVariables } = this.state;
    return (
      <Mutation
        mutation={BLOCK_USERS_MUTATION}
        onCompleted={this.mutationCompleted(
          'deactivateUsers',
          itemProcessing,
          lastVariables,
        )}
      >
        {handleUserBlock => render({ handleUserBlock })}
      </Mutation>
    );
  };

  /**
   * @description
   * Mutation block callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  userUnBlock = ({ render }) => {
    const { itemProcessing, lastVariables } = this.state;
    return (
      <Mutation
        mutation={ACTIVE_USERS_MUTATION}
        onCompleted={this.mutationCompleted(
          'activateUsers',
          itemProcessing,
          lastVariables,
        )}
      >
        {handleUserUnBlock => render({ handleUserUnBlock })}
      </Mutation>
    );
  };

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} filters - Filters to apply.
   */
  handleFilter = filters => {
    const history = this.context;
    this.setState({
      filters,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...filters,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {string} text - Text to be updated.
   */
  handleTextChanged = text => {
    this.setState({ textToSearch: text });
  };

  /**
   * @description
   * OnComplete mutations.
   *
   * @param {Object} field - Target field of mutation.
   * @param {Object} itemProcessing - Ids of items that is processing.
   * @returns {undefined} - No returns value.
   */
  mutationCompleted = (field, itemProcessing) => async data => {
    const failedIds = itemProcessing.filter(
      item => !data[field].successIds.includes(item),
    );
    this.setState({
      itemProcessing: [],
      failedIds,
      showNotification: failedIds.length > 0,
    });
    this.fetchMore({
      updateQuery: (previousResult, options) => options.fetchMoreResult,
    });
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Query helpful function to update query's result.
   *
   * @param {Object} previousResult - Latest Query result.
   * @param {Object} options - UpdateQuery options.
   * @returns {Object} New or Previous query's result.
   */
  updateQuery = (previousResult, options) => {
    const { pageInfo, edges: newEdges } = options.fetchMoreResult.adminUsers;
    if (newEdges.length !== 0) {
      return {
        adminUsers: {
          __typename: previousResult.adminUsers.__typename,
          edges: [...previousResult.adminUsers.edges, ...newEdges],
          pageInfo,
        },
      };
    }
    return previousResult;
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  /**
   * @description
   * Close notification.
   * @returns {undefined} - No returns value.
   */
  handleCloseNotification = () => {
    this.setState({ showNotification: false });
  };

  /**
   * @description
   * Render UserList component.
   */
  render() {
    const {
      numberOfRowsSelected,
      filters,
      itemProcessing,
      textToSearch,
      pagination,
      failedIds,
      showNotification,
    } = this.state;
    return (
      <this.Compose>
        {({
          mutateUserDelete: { handleUserDelete },
          mutateUserBlock: { handleUserBlock },
          mutateUserUnBlock: { handleUserUnBlock },
          query: { data, fetchMore, loading },
        }) => (
          <UserList
            data={data}
            appliedFilter={filters}
            paginationDirection={pagination.direction}
            textToSearch={textToSearch}
            textChanged={this.handleTextChanged}
            filter={this.handleFilter}
            userDelete={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleUserDelete({ variables: { ids: items } });
            }}
            userBlock={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleUserBlock({ variables: { ids: items } });
            }}
            userUnBlock={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
              });
              handleUserUnBlock({ variables: { ids: items } });
            }}
            numberOfRowsSelected={numberOfRowsSelected}
            changePage={paginatorVariables => {
              this.handleChangePage(paginatorVariables, fetchMore);
            }}
            refresh={this.handleRefresh}
            changeNumberOfRows={this.handleChangeNumberOfRows}
            loading={Object.keys(data).length === 0}
            fullLoading={loading}
            itemProcessing={itemProcessing}
            failedIds={failedIds}
            showNotification={showNotification}
            closeNotification={this.handleCloseNotification}
          />
        )}
      </this.Compose>
    );
  }
}

/**
 * UserListContainer PropTypes
 * @param me - prop current logged user
 * @param initialFilters - prop initial Filters to apply
 */

UserListContainer.propTypes = {
  me: PropTypes.shape({}).isRequired,
  initialFilters: PropTypes.shape({}).isRequired,
};

export default UserListContainer;
